import { useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falArrowRight } from 'shared/@generated/icons/falArrowRight';
import { falClock } from 'shared/@generated/icons/falClock';
import { falPaw } from 'shared/@generated/icons/falPaw';
import { falTrashAlt } from 'shared/@generated/icons/falTrashAlt';
import { ConfirmModal, type ConfirmModalRef } from 'shared/components/confirm-modal';
import { type BaseModalRef } from 'shared/components/base-modal';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';
import { type AdvisorTranslate } from 'shared/library/breed-advisor';
import { type AdvisorHistoryEntry } from 'shared/library/breed-advisor-history';

export function BreedAdvisorHistory({
  history,
  activeId,
  disabled,
  onOpen,
  onRemove,
  copy,
}: {
  history: AdvisorHistoryEntry[];
  activeId: string | null;
  disabled?: boolean;
  onOpen: (id: string) => void;
  onRemove: (id: string) => void;
  copy: AdvisorTranslate;
}) {
  const { i18n } = useTranslation();
  const modal = useRef<BaseModalRef>(null);
  const confirm = useRef<ConfirmModalRef>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const date = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  useEffect(() => {
    if (
      pendingDelete &&
      (disabled ||
        pendingDelete === activeId ||
        !history.some((entry) => entry.id === pendingDelete))
    ) {
      confirm.current?.close();
      setPendingDelete(null);
    }
  }, [activeId, disabled, history, pendingDelete]);

  return (
    <>
      <View
        testID="advisor-history-section"
        className="min-h-12 flex-row items-center justify-between gap-2 bg-white px-4"
      >
        <View className="flex-row items-center gap-2.5">
          <Icon icon={falClock} className="text-brand-brown-dark/75 size-4" />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={copy('history_title')}
            testID="advisor-history-trigger"
            disabled={disabled}
            onPress={() => modal.current?.open()}
            className="focus-visible:outline-brand-green group cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-50"
          >
            <Text className="text-brand-brown-dark/75 group-hover:text-brand-brown-dark text-xs underline-offset-4 transition-colors group-hover:underline group-focus-visible:underline">
              {copy('history_title')}
            </Text>
          </Pressable>
        </View>
        <View className="shrink-0 flex-row items-center gap-2">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`${copy('history_saved', { count: history.length })}, ${copy('history_title')}`}
            testID="advisor-history-count-trigger"
            disabled={disabled}
            onPress={() => modal.current?.open()}
            className="focus-visible:outline-brand-green group cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-50"
          >
            <Text className="text-brand-brown-dark/55 group-hover:text-brand-brown-dark text-xs underline-offset-4 transition-colors group-hover:underline group-focus-visible:underline">
              {copy('history_saved', { count: history.length })}
            </Text>
          </Pressable>
          <Icon icon={falAngleRight} className="text-brand-brown-dark/75 size-3.5" />
        </View>
      </View>

      <FullscreenModal
        ref={modal}
        centered
        headerTitle={copy('history_title')}
        contentTestID="advisor-history-modal"
      >
        {history.length ? (
          <View testID="advisor-history-list" className="divide-brand-brown-dark/10 divide-y">
            {history.map((entry) => {
              const label = date.format(new Date(entry.updatedAt));
              const breed = entry.topBreeds[0];
              const current = entry.id === activeId;
              return (
                <View
                  key={entry.id}
                  testID={`advisor-history-entry-${entry.id}`}
                  className="flex-row items-start gap-4 py-5 first:pt-0 last:pb-0 sm:items-center"
                >
                  {breed?.avatar ? (
                    <Image
                      src={breed.avatar}
                      alt={breed.name}
                      containerClassName="size-16 shrink-0 overflow-hidden rounded"
                      imageClassName="object-cover"
                      sizeType="avatar"
                      aspectRatio={1}
                      sizes="64px"
                    />
                  ) : (
                    <View className="border-brand-brown-dark/15 size-16 shrink-0 items-center justify-center rounded border bg-white">
                      <Icon
                        icon={entry.hasResults ? falPaw : falClock}
                        className="text-brand-brown-dark/50 size-6"
                      />
                    </View>
                  )}

                  <View className="min-w-0 flex-1 gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <View className="min-w-0 sm:flex-1">
                      <Text className="text-brand-brown-dark/65 text-xs leading-5 sm:text-sm">
                        {label}
                        {current ? ` · ${copy('history_current')}` : ''}
                      </Text>
                      <Text className="text-brand-brown-dark mt-1 text-sm font-semibold leading-6 sm:text-base">
                        {entry.hasResults
                          ? entry.topBreeds.map((item) => item.name).join(', ') ||
                            copy('history_untitled')
                          : copy('history_questions', { count: entry.answerCount })}
                      </Text>
                    </View>

                    <View className="flex-row items-center justify-between gap-2 sm:justify-end">
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`${copy('history_open')}: ${label}`}
                        testID={`advisor-history-open-${entry.id}`}
                        disabled={disabled}
                        onPress={() => {
                          modal.current?.close();
                          onOpen(entry.id);
                        }}
                        className="hover:border-brand-brown-dark/45 focus-visible:outline-brand-green border-brand-brown-dark/20 group min-h-11 flex-row items-center justify-center gap-2 self-start rounded border bg-white px-4 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 max-sm:border-transparent max-sm:px-0"
                      >
                        <Text className="text-brand-brown-dark decoration-brand-brown-dark/35 group-hover:decoration-brand-brown-dark text-sm font-semibold max-sm:underline max-sm:underline-offset-4">
                          {copy(entry.hasResults ? 'history_view_results' : 'history_continue')}
                        </Text>
                        <Icon icon={falArrowRight} className="text-brand-brown-dark size-3.5" />
                      </Pressable>
                      {!current && (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`${copy('history_delete')}: ${label}`}
                          testID={`advisor-history-delete-${entry.id}`}
                          disabled={disabled}
                          className="focus-visible:outline-brand-green group size-11 shrink-0 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                          onPress={() => {
                            setPendingDelete(entry.id);
                            confirm.current?.open();
                          }}
                        >
                          <Icon
                            icon={falTrashAlt}
                            className="text-brand-brown-dark/45 group-hover:text-brand-brown-dark size-4"
                          />
                        </Pressable>
                      )}
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        ) : (
          <Text
            testID="advisor-history-empty"
            className="text-brand-brown-dark/65 py-8 text-sm leading-6"
          >
            {copy('history_empty')}
          </Text>
        )}
      </FullscreenModal>

      <ConfirmModal
        ref={confirm}
        title={copy('history_delete_title')}
        description={copy('history_delete_description')}
        confirmLabel={copy('history_delete_confirm')}
        cancelLabel={copy('history_cancel')}
        onConfirm={async () => {
          if (
            !disabled &&
            pendingDelete &&
            pendingDelete !== activeId &&
            history.some((entry) => entry.id === pendingDelete)
          ) {
            onRemove(pendingDelete);
          }
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
